import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { formatCuitDisplay } from './cuit-text-field';

export type GrowerIdentifier = 'cuit' | 'email';

export type IdentifierFieldStatus =
    | 'idle'
    | 'pending'
    | 'verifying'
    | 'valid'
    | 'duplicate'
    | 'invalidFormat'
    | 'connectionError';

/**
 * Canonical form used for comparisons and for what we send to the API: lower-cased and
 * trimmed for email, digits only for CUIT. Both match how the backend normalizes them.
 */
export function normalizeGrowerIdentifier(identifier: GrowerIdentifier, value: string): string {
    return identifier === 'email' ? value.trim().toLowerCase() : value.replace(/\D/g, '');
}

/** Copy and display helpers for tenant grower identifier (CUIT vs email). */
export function getGrowerIdentifierCopy(identifier: GrowerIdentifier) {
    const appName = settings.app.name;
    const isEmail = identifier === 'email';

    return {
        step1Title: isEmail ? _('1. Validá el correo del productor') : _('1. Validá el CUIT del productor'),
        step1Subtitle: _('Para verificar si ya está creado en {{appName}}.', { appName }),
        fieldLabel: isEmail ? _('Email') : _('CUIT'),
        fieldHelp: isEmail
            ? _('Ingresá el correo del productor. Ej: productor@mail.com')
            : _('Ingresá el número de CUIT sin guiones'),
        validateButton: isEmail ? _('Validar correo') : _('Validar CUIT'),
        changeButton: isEmail ? _('Cambiar correo') : _('Cambiar CUIT'),
        verifying: isEmail ? _('Verificando correo…') : _('Verificando CUIT…'),
        valid: (businessName?: string) => {
            if (isEmail) return _('Correo válido');
            if (businessName) return _('CUIT válido — {{name}}', { name: businessName });
            return _('CUIT válido');
        },
        alreadyRegistered: isEmail
            ? _('Este correo ya está registrado en {{appName}}.', { appName })
            : _('Este CUIT ya está registrado en {{appName}}.', { appName }),
        invalidFormat: isEmail
            ? _('Ingresá un correo válido. Ej: productor@mail.com')
            : _('El CUIT tiene que tener 11 dígitos.'),
        connectionError: isEmail
            ? _('No pudimos verificar el correo ahora. Reintentá en unos segundos.')
            : _('No pudimos verificar el CUIT ahora. Reintentá en unos segundos.'),
        identifierWord: isEmail ? _('correo') : _('CUIT'),
        identifierWordCapitalized: isEmail ? _('Correo') : _('CUIT'),
    };
}

export function isIdentifierStatusBlockingSave(status: IdentifierFieldStatus): boolean {
    return (
        status === 'pending' ||
        status === 'verifying' ||
        status === 'duplicate' ||
        status === 'invalidFormat' ||
        status === 'connectionError'
    );
}

export function formatGrowerIdentifierDisplay(identifier: GrowerIdentifier, value: string): string {
    if (identifier === 'cuit') {
        return formatCuitDisplay(value);
    }
    return value;
}

export function growerIdentifierStep2AvailableMessage(identifier: GrowerIdentifier, value: string): string {
    const display = formatGrowerIdentifierDisplay(identifier, value);
    if (identifier === 'email') {
        return _('El correo {{value}} no está registrado. Completá los datos del productor:', {
            value: display,
        });
    }
    return _('El CUIT {{cuit}} no está registrado. Completá los datos del productor:', {
        cuit: display,
    });
}

export function growerIdentifierStep2EditMessage(identifier: GrowerIdentifier, value: string): string {
    const display = formatGrowerIdentifierDisplay(identifier, value);
    if (identifier === 'email') {
        return _('Editá los datos del productor con correo {{value}}:', { value: display });
    }
    return _('Editá los datos del productor con CUIT {{cuit}}:', { cuit: display });
}

export function growerIdentifierAlertPrefix(identifier: GrowerIdentifier, value: string): string {
    const display = formatGrowerIdentifierDisplay(identifier, value);
    if (identifier === 'email') {
        return _('(correo: {{value}})', { value: display });
    }
    return _('(CUIT: {{cuit}})', { cuit: display });
}

export function growerIdentifierSidebarLabel(identifier: GrowerIdentifier, value: string): string {
    const display = formatGrowerIdentifierDisplay(identifier, value);
    if (identifier === 'email') {
        return display;
    }
    return `CUIT ${display}`;
}
